import {
  isInteger,
  isString,
  pick,
  forEach,
  isUndefined,
  isEmpty,
} from 'lodash';
import { isValidURL } from 'utils/isValidURL';
import { isPastDate, getNHoursInMs } from 'components/SchedulingForm/utils';
import classNames from 'classnames';
import {
  VALIDATION_ERRORS,
  SURVEY_END_PAGE_CUSTOM,
  SURVEY_LINK_EXPIRE_OPTIONS,
  postValues,
  LINK_EXPIRE_OFFSET_HOURS,
  SURVEY_END_PAGE_OPTIONS,
  VARIABLES,
} from './constants';

const isNullOrEmptyString = (value) => !isString(value) || value === '';

const containsUrlRegex =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
export const containsUrl = (text) => new RegExp(containsUrlRegex).test(text);

export const getFieldValidationErrors = ({
  name,
  value,
  settings = {},
  completed = 0,
}) => {
  switch (name) {
    case VARIABLES.MAX_RESPONSES: {
      if (value === null) {
        return false;
      }
      const isInt = isInteger(value);
      const notPositiveInteger =
        isUndefined(value) ||
        (isString(value) && value === '') ||
        (isInt && value <= 0);
      const canReceiveCompletes = isInt && value > completed;
      if (notPositiveInteger) {
        return VALIDATION_ERRORS.MAX_RESPONSES;
      }
      if (!canReceiveCompletes) {
        return VALIDATION_ERRORS.MAX_RESPONSES_LIMIT;
      }

      return false;
    }
    case VARIABLES.LINK_EXPIRE: {
      if (settings.linkExpireSelect === SURVEY_LINK_EXPIRE_OPTIONS[0].value) {
        return false;
      }

      const completeDate =
        settings.linkExpireTime &&
        settings.expireTimezone &&
        settings.linkExpireDate;

      if (!completeDate) {
        return VALIDATION_ERRORS.MISSING_DATE;
      }

      return isPastDate({
        selectedDate: value,
        time: settings.linkExpireTime,
        timezone: settings.expireTimezone,
        offset: getNHoursInMs(LINK_EXPIRE_OFFSET_HOURS),
      })
        ? VALIDATION_ERRORS.PAST_DATE
        : false;
    }
    case VARIABLES.CUSTOM_THANK_YOU_MESSAGE: {
      if (!isString(value) || value.trim()) {
        if (containsUrl(value)) {
          return VALIDATION_ERRORS.CONTAINS_URL;
        }

        return false;
      }

      return VALIDATION_ERRORS.THANK_YOU_MESSAGE;
    }
    case VARIABLES.QUALITY_TERMINATE_URL:
    case VARIABLES.PROFILING_TERMINATE_URL:
    case VARIABLES.OVERQUOTA_TERMINATE_URL: {
      const { surveyEndPage } = settings;
      if (surveyEndPage === SURVEY_END_PAGE_CUSTOM) {
        if (value === null || value === undefined) {
          return false;
        }

        if (value === '') {
          return VALIDATION_ERRORS.EMPTY_URL;
        }

        if (value && isValidURL(value)) {
          return false;
        }

        return VALIDATION_ERRORS.INVALID_URL;
      }

      return false;
    }
    case VARIABLES.SURVEY_COMPLETED_URL:
    case VARIABLES.SCREENOUT_URL: {
      const { surveyEndPage } = settings;
      if (surveyEndPage === SURVEY_END_PAGE_CUSTOM) {
        if (isNullOrEmptyString(value)) {
          return VALIDATION_ERRORS.EMPTY_URL;
        }

        if (value && isValidURL(value)) {
          return false;
        }

        return VALIDATION_ERRORS.INVALID_URL;
      }

      return false;
    }
    default:
      return false;
  }
};

export const getValidationErrors = (settings, completed = 0) => {
  if (isEmpty(settings)) {
    return false;
  }
  const validationErrors = {};
  forEach(postValues, (name) => {
    validationErrors[name] = getFieldValidationErrors({
      name,
      value: settings[name],
      settings,
      completed,
    });
  });
  return validationErrors;
};

export const validDate = (current) => {
  const today = Date.now();
  if (current) {
    const dateAsMs = current.valueOf();
    const isInThePast = dateAsMs < today && !current.isSame(today, 'day');
    return isInThePast;
  }
  return false;
};

export const getPostValues = (settings) => pick(settings, postValues);

export const getInputClasses = (disabled = false) =>
  classNames(
    { 'background-grey-4': !disabled },
    { 'background-grey-3': disabled },
    { 'color-grey-10': !disabled },
    { 'color-grey-8': disabled },
    { 'cursor-default': disabled },
    { 'cursor-not-allowed': disabled },
    'border-none',
    'ant-input-shadow-none',
    'w-full',
    'link-settings-input',
    'color-grey-7-important-placeholder',
  );

export const getInputLabelClasses = (disabled = false) =>
  classNames(
    { 'color-grey-10': !disabled },
    { 'color-grey-7': disabled },
    'text-size-secondary',
    'display-block',
  );

export const getToggleClasses = (disabled = false) =>
  classNames('padding-horizontal-lg', 'padding-vertical-md', {
    'cursor-not-allowed': disabled,
  });
export const getToggleLabelClasses = (disabled = false) =>
  classNames({
    'cursor-not-allowed': disabled,
  });
export const getValidationMessage = ({ messages, name, validationErrors }) => {
  const propSpecificMsg = messages.errors[validationErrors[name]];

  return propSpecificMsg || messages.errors[VALIDATION_ERRORS.GENERIC];
};

export const getSurveyEndpageOptions = (includeProdegeOptions) => {
  if (includeProdegeOptions) {
    return SURVEY_END_PAGE_OPTIONS;
  }
  return SURVEY_END_PAGE_OPTIONS.slice(0, 3);
};
